import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import { useMemo } from 'react';
import { get, useFieldArray, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import PenFilledIcon from '../../../../../../../../../../../../assets/icons/PenFilled';
import { IManualInputMaterialData } from '../../../../../../../../../../../../components/dialog-manager/dialogs/enter-manual-input-material-dialog/EnterManualInputMaterialDialog';
import ErrorMessage from '../../../../../../../../../../../../components/error-message';
import useDialogContext from '../../../../../../../../../../../../hooks/dialog/useDialogContext';
import CustomIcon from '../../../../../../../../../../../../shared/components/custom-icon/CustomIcon';
import { MAX_INPUT_MATERIALS_PER_PROCESS } from '../../../../../../../../../../../../shared/constants/products.constant';
import { IComponent } from '../../../../../../../../../../../../shared/interfaces/IComponent';
import { IInputMaterial } from '../../../../PcfWizard.type';
import InputMaterialCard from './components/input-material-card/InputMaterialCard';
import { mapComponentToInputMaterial, mapManualDataToInputMaterial } from './helpers';

interface InputMaterialsSectionProps {
  name: string;
  processIndex: number;
}

/**
 * Up to 30 input materials of an Individual Process, each with its own amount, waste/scrap
 * and waste treatment (PAC-1611/PAC-1615).
 *
 * Injection Molding is explicitly out of scope for this — it keeps the old single-slot
 * `InputMaterial` component (`input_materials[0]`, process-level sprue/scrap waste management)
 * in the sibling `input-material` directory. This component owns a `useFieldArray` instead of
 * a single `useController`, which is why it lives separately rather than extending that one.
 */
const InputMaterialsSection = ({ name, processIndex }: InputMaterialsSectionProps) => {
  const { t } = useTranslation();
  const { openDialog } = useDialogContext();
  const {
    control,
    formState: { submitCount },
    trigger,
  } = useFormContext();

  const basePath = `${name}[${processIndex}].input_materials`;

  const { fields, append, remove } = useFieldArray({ control, name: basePath });
  const materials = (useWatch({ control, name: basePath }) ?? []) as IInputMaterial[];

  // The empty-array error has no field of its own to focus, so it is read here and rendered
  // under the section title.
  const { errors } = useFormState({ control, name: basePath });
  const sectionError: string | undefined = get(errors, `${basePath}.message`);

  const freeSlots = MAX_INPUT_MATERIALS_PER_PROCESS - fields.length;
  const isFull = freeSlots <= 0;

  // Components already in THIS process. The same component may be used in another process.
  const alreadyAddedProductIds = useMemo(() => {
    return materials
      .filter((material) => {
        return material?.id != null;
      })
      .map((material) => {
        return material.id as number;
      });
  }, [materials]);

  const handleAddFromComponents = () => {
    openDialog('select-input-materials-dialog', {
      freeSlots,
      alreadyAddedProductIds,
      onSelect: (components: IComponent[]) => {
        append(components.map(mapComponentToInputMaterial));
      },
    });
  };

  const handleAddManually = () => {
    openDialog('enter-manual-input-material-dialog', {
      onAdd: (data: IManualInputMaterialData) => {
        append(mapManualDataToInputMaterial(data));
      },
    });
  };

  const handleRemove = (index: number) => {
    remove(index);

    // Removing an item leaves the array-level error stale, so re-check once submitted.
    if (submitCount > 0) {
      trigger(basePath);
    }
  };

  return (
    <div className='surface-ground border-round-2xl p-3 mb-3'>
      <div className='flex flex-row w-full align-items-center gap-3 mb-3'>
        <h3 className='m-0 font-medium' style={{ fontSize: '16px' }}>
          {t('footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.titlePlural')}
        </h3>
        <span className='text-sm text-gray-700'>
          {t('footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.counter', {
            count: fields.length,
            max: MAX_INPUT_MATERIALS_PER_PROCESS,
          })}
        </span>
      </div>

      {sectionError && (
        <div className='py-2' data-pcf-error-anchor='true'>
          <ErrorMessage message={sectionError} />
        </div>
      )}

      {fields.length === 0 ? (
        <div className='font-light text-black-alpha-60 mb-3'>
          {t('footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.emptyState', {
            max: MAX_INPUT_MATERIALS_PER_PROCESS,
          })}
        </div>
      ) : (
        fields.map((field, index) => {
          return (
            <InputMaterialCard
              key={field.id}
              inputMaterial={materials[index] ?? {}}
              formPath={`${basePath}[${index}]`}
              processIndex={processIndex}
              onRemove={() => {
                return handleRemove(index);
              }}
            />
          );
        })
      )}

      <div className='flex align-items-center gap-4'>
        <Button
          label={t(
            'footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.addInputMaterialText',
          )}
          className='p-button-primary'
          icon='pi pi-plus-circle'
          iconPos='left'
          disabled={isFull}
          pt={{ icon: { className: 'text-xl' } }}
          onClick={handleAddFromComponents}
        />

        <span className='font-sm text-gray-400'>
          {t('footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.orText')}
        </span>

        <Button
          className='p-0 p-button-text flex flex-row align-items-center'
          disabled={isFull}
          onClick={handleAddManually}
        >
          {/* Disabled: follow the button's own disabled text color instead of the primary one */}
          <CustomIcon
            icon={PenFilledIcon}
            iconOptions={isFull ? { fill: 'currentColor' } : undefined}
            className='flex w-10 p-0 h-full'
          />
          <span
            className={classNames('font-medium text-center h-full', { 'text-primary': !isFull })}
          >
            {t(
              'footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.enterManuallyText',
            )}
          </span>
        </Button>

        {isFull && (
          <span className='font-medium text-error'>
            {t(
              'footprintDetailsPage.sections.calculatePcfSection.inputMaterialSection.maxReachedText',
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default InputMaterialsSection;
